import React, { useEffect, useState } from 'react';
import { FreelancerSkillExperience, IFreelancer, missingFieldsForCompletion, RestrictedFreelancerRole } from '../../../data_models/freelancer';

import { DEFAULT_USER_PROFILE } from '../../../config/defaultAssetUrls';
import Avatar from '../../common/Avatar';

import Card from '../../common/Card';
import useCurrentUser from '../../../hooks/useCurrentUser';
import useGlobalLoading from '../../../hooks/useGlobalLoading';
import { useFilePicker } from 'use-file-picker';
import useAuth from '../../../hooks/useAuth';
import useDisplayNotifications from '../../../hooks/useDisplayNotifications';
import { DisplayNotificationType } from '../../../models/DisplayNotification';
import { getFreelancerName } from '../../../utils/freelancerUtils';
import { FilledButton, FlatButton, OutlinedButton } from '../../common/Button';
import ProfileLabelValue from '../common/ProfileLabelValue';
import { IoIosCheckmarkCircleOutline, IoMdCloseCircleOutline } from 'react-icons/io';
import { fullAddressString } from '../../../utils/addressUtils';
import { UserEnumTextMapper } from '../../../data_models/enumToTextMapper';
import { UpgradeToPmLightbox } from '../../lightboxes/profile/UpgradeToPmLightbox';
import { FiEdit3 } from "react-icons/fi";
import { FreelancerWallet } from '../../../data_models/freelancerWallet';
import useConfig from '../../../hooks/useConfig';
import SkillExperience from '../common/SkillExperience';
import { Chip } from '../../common/Chip';
import { EditPersonalInfoFreelancer } from '../../lightboxes/profile/EditPersonalInfoFreelancer';
import { EditProfessionalInfoFreelancer } from '../../lightboxes/profile/EditProfessionalInfoFreelancer';
import { set } from 'react-datepicker/dist/date_utils';
import { EditSocialLinksFreelancer } from '../../lightboxes/profile/EditSocialLinksFreelancer';
import { EditLegalAndTaxFreelancer } from '../../lightboxes/profile/EditLegalAndTaxFreelancer';
import { countriesToTextMapper } from '../../../data_models/data/countries';
import InfoBox, { ErrorBox } from '../../common/InfoBox';
import ProgressBar from '../../common/ProgressBar';
import RichText from '../../common/RichText';
import { ChatNowButton, InviteForTasksButton, ShortlistFreelancerButton } from '../../common/GhButtons';
import { EUserType } from '../../../data_models/enums';



interface FreelancerUserProfileProps {
  freelancer: IFreelancer;
  isCurrentUser: boolean;
}

const FreelancerUserProfile: React.FC<FreelancerUserProfileProps> = ({ freelancer: _freelancer, isCurrentUser }) => {

  const [showEditPersonalInfoLightbox, setShowEditPersonalInfoLightbox] = useState(false);
  const [showEditSocialLinksLightbox, setShowEditSocialLinksLightbox] = useState(false);
  const [showEditLegalAndTaxLightbox, setShowEditLegalAndTaxLightbox] = useState(false);
  const [showEditProfessionalInfoLightbox, setShowEditProfessionalInfoLightbox] = useState(false);
  const [showUpgradeToPmLightbox, setShowUpgradeToPmLightbox] = useState(false);

  const {
    error,
    currentUser,
    currentUserType,
    updateProfilePicture,
  } = useCurrentUser();
  const [freelancer, setFreelancer] = useState<IFreelancer>(_freelancer)
  const [profileCompletedPercentage, setProfileCompletedPercentage] = useState<number>(freelancer?._profileCompletedPercentage || 0)

  const { sendVerificationEmail } = useAuth();
  const { addNotification } = useDisplayNotifications();

  const { openFilePicker, filesContent, plainFiles, loading } = useFilePicker({
    accept: ['image/*'], // Accept only image files
    multiple: false, // Allow only one file
  });

  const resendEmailVerification = () => {
    if (freelancer.email) {
      sendVerificationEmail().then(() => {
        addNotification(
          {
            message: 'Verification email sent',
            type: DisplayNotificationType.SUCCESS
          },
          true
        )
      }).catch(() => {
        addNotification(
          {
            message: 'An error occurred while sending the verification email',
            type: DisplayNotificationType.ERROR
          },
          true
        )
      });
    }
  }

  useEffect(() => {
    if (isCurrentUser) {
      setFreelancer(currentUser as IFreelancer);
    }
  }, [currentUser, _freelancer]);

  useEffect(() => {
    // Handle the selected file after the file picker is closed
    if (filesContent.length > 0) {
      // const file = filesContent[0];
      const plain = plainFiles[0];

      updateProfilePicture(plain).then((user) => {
      }).catch((e) => {
        addNotification(
          {
            message: error || 'An error occurred while updating the profile picture',
            type: DisplayNotificationType.ERROR
          },
          true
        )
      });
    }
  }, [filesContent]);




  return (
    <div className='p-4 space-y-4 max-w-7xl mx-auto'>
      {/* Notification boxes */}
      {isCurrentUser && currentUser && <div className='flex flex-col space-y-4'>

        {/* Email Verification */}
        {!currentUser._emailVerified && <ErrorBox>
          <div className='mtflex flex-col space-y-2'>
            <div className='flex flex-wrap items-center gap-x-4 gap-y-2'>
              <h6 className='font-bold'>Please verify your email</h6>
              <p className='body2'>To enable full functionality, please verify your email.</p>
              <FilledButton
                theme='danger'
                onClick={resendEmailVerification}>
                Resend Verification Email
              </FilledButton>
            </div>
          </div>
        </ErrorBox>}

        {/* Profile Completion */}
        {((currentUser as IFreelancer)._profileCompletedPercentage || 0) < 100 && <InfoBox>
          <div className='flex flex-col space-y-2'>
            <div className='flex flex-wrap items-center gap-x-4 gap-y-2'>
              <h6 className='font-bold'>Profile Completion</h6>
              <p className='body2'>For better visibility and reputation, please complete your profile.</p>
            </div>

            <div className='flex flex-row items-center space-x-2 mt-4'>
              <ProgressBar
                highestValue={100}
                lowestValue={0}
                actualValue={freelancer?._profileCompletedPercentage || 0}
              />
              <p>
                {freelancer?._profileCompletedPercentage || 0}%
              </p>
            </div>


            <p className='body3 font-bold'>Missing Information</p>
            <ul>
              {missingFieldsForCompletion(freelancer).map((field) => (
                <li key={field}>{field}</li>
              ))}
            </ul>
          </div>
        </InfoBox>}


      </div>}
      <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
        {/* Left (mobile upper) columns */}
        <div>
          {/* Personal Information */}
          <Card>
            <div className='my-4 flex flex-col'>

              {/* Profile Avatar section */}
              <div className='flex flex-col items-center'>
                <Avatar
                  imageUrl={
                    freelancer?.profilePictureUrl ||
                    DEFAULT_USER_PROFILE.DEFAULT_FREELANCER_PROFILE_PICTURE_MALE
                  }
                  onUpload={isCurrentUser ? openFilePicker : undefined}
                />
                <p className='mt-4 body1 font-bold'>{getFreelancerName(freelancer)}</p>
                {/* Languages */}
                {(freelancer.languages?.length || 0) > 0 && <p className='body3'>{freelancer.languages?.map(language => language).join(', ')}</p>}
                {/* Email if current user - roles if not current user */}
                {isCurrentUser && <p className='body3 text-primary-700'>{freelancer.email}</p>}
                {/* Country if is other user */}
                {!isCurrentUser && freelancer.country && <p className='body3 text-primary-700'>{countriesToTextMapper(freelancer.country)}</p>}


                {/* Chat now / Invite button / Shortlist button */}
                <div className='mt-4 flex flex-row space-x-2'>
                  {!isCurrentUser && <ChatNowButton user={freelancer} />}
                  {!isCurrentUser &&
                    currentUserType === EUserType.FREELANCER &&
                    (currentUser as IFreelancer)?.restrictedRoles?.includes(RestrictedFreelancerRole.PROJECT_MANAGER) &&
                    <InviteForTasksButton freelancer={freelancer} />}
                  {!isCurrentUser &&
                    (currentUserType === EUserType.FREELANCER &&
                    (currentUser as IFreelancer)?.restrictedRoles?.includes(RestrictedFreelancerRole.PROJECT_MANAGER)
                    || currentUserType === EUserType.EMPLOYER) && <ShortlistFreelancerButton freelancer={freelancer} />}
                </div>
              </div>

              {/* Basic Information */}
              {isCurrentUser && <div className='mt-6'>
                <PersonalInformation
                  freelancer={freelancer}
                  isCurrentUser={isCurrentUser}
                  setShowEditPersonalInfoLightbox={setShowEditPersonalInfoLightbox} />
                {/* PM intent */}
                {!freelancer.restrictedRoles?.includes(RestrictedFreelancerRole.PROJECT_MANAGER)
                  && !freelancer.projectManagerIntent && <div className='mt-2'>
                    <OutlinedButton fullWidth onClick={() => { setShowUpgradeToPmLightbox(true) }}>Upgrade Status to PM</OutlinedButton></div>}
                {(!freelancer.restrictedRoles?.includes(RestrictedFreelancerRole.PROJECT_MANAGER)
                  && freelancer.projectManagerIntent === true) &&
                  <p className='mt-4 text-neutral-700'>
                    We received your application as PM
                  </p>}
              </div>}

              {/* Separator */}
              <hr className='m-4 border-neutral-300' />

              {/* Social Links */}
              <div>
                <SocialLinks freelancer={freelancer}
                  isCurrentUser={isCurrentUser}
                  setShowEditSocialLinksLightbox={setShowEditSocialLinksLightbox} />
              </div>

              {/* Separator */}
              {isCurrentUser && <hr className='m-4 border-neutral-300' />}

              {/* Others */}
              {isCurrentUser && <div>
                <BusinessInfo freelancer={freelancer}
                  isCurrentUser={isCurrentUser}
                  setShowEditLegalAndTaxLightbox={setShowEditLegalAndTaxLightbox} />
              </div>}
            </div>
          </Card>

        </div>

        {/* Right (mobile lower) columns */}
        <div className='flex flex-col space-y-4'>
          {isCurrentUser && <Card><WalletInfo freelancer={freelancer} /></Card>}

          <Card>
            <ProfessionalInfo
              freelancer={freelancer}
              isCurrentUser={isCurrentUser}
              setShowEditProfessionalInfoLightbox={setShowEditProfessionalInfoLightbox} />
          </Card>
        </div>
      </div >


      {showUpgradeToPmLightbox && <UpgradeToPmLightbox onClose={() => setShowUpgradeToPmLightbox(false)} />}
      {showEditPersonalInfoLightbox && <EditPersonalInfoFreelancer onClose={() => setShowEditPersonalInfoLightbox(false)} />}
      {showEditProfessionalInfoLightbox && <EditProfessionalInfoFreelancer onClose={() => setShowEditProfessionalInfoLightbox(false)} />}
      {showEditSocialLinksLightbox && <EditSocialLinksFreelancer onClose={() => setShowEditSocialLinksLightbox(false)} />}
      {showEditLegalAndTaxLightbox && <EditLegalAndTaxFreelancer onClose={() => setShowEditLegalAndTaxLightbox(false)} />}
    </div >
  );
};


const PersonalInformation: React.FC<{ freelancer: IFreelancer, isCurrentUser: boolean, setShowEditPersonalInfoLightbox: (show: boolean) => void }> = ({ freelancer, isCurrentUser, setShowEditPersonalInfoLightbox }) => {
  return (
    <div className='flex flex-col space-y-1'>
      <div className='flex flex-row justify-between items-center'>
        <p className='body2 text-primary-700'>Personal Information</p>
        {isCurrentUser && <OutlinedButton
          icon={<FiEdit3 />}
          iconPosition='leading'
          size='small'
          onClick={
            () => {
              setShowEditPersonalInfoLightbox(true);
            }}>
          Edit</OutlinedButton>}
      </div>


      {/* Full Name */}
      <ProfileLabelValue label='Full Name' value={getFreelancerName(freelancer)} />
      <div className='flex flex-row space-x-4'>
        <ProfileLabelValue label='Email' value={freelancer.email || ''} />
        {freelancer._emailVerified
          ? <div className='mt-auto flex flex-row space-x-2 text-green-500 items-center'>
            <p>Verified</p>
            <IoIosCheckmarkCircleOutline />
          </div>
          : <div className='mt-auto flex flex-row space-x-2 text-red-500 items-center'>
            <p>Not yet Verified</p>
            <IoMdCloseCircleOutline />
          </div>}
      </div>
      <ProfileLabelValue label='Phone' value={freelancer.phone || 'Not set'} />
      {/* <ProfileLabelValue label='Date of Birth' value={freelancer.birthDate?.toLocaleDateString() || 'Not set'} /> */}
      <ProfileLabelValue label='Address' value={fullAddressString(
        freelancer.streetAddress,
        freelancer.streetAddress2,
        freelancer.city,
        freelancer.state,
        freelancer.zipCode,
        freelancer.country)
        || 'Not set'} />
    </div>
  );
}

const SocialLinks: React.FC<{ freelancer: IFreelancer, isCurrentUser: boolean, setShowEditSocialLinksLightbox: (show: boolean) => void }> = ({ freelancer, isCurrentUser, setShowEditSocialLinksLightbox }) => {
  return (
    <div className='flex flex-col space-y-1'>
      <div className='flex flex-row justify-between items-center'>
        <p className='body2 text-primary-700'>Social Links</p>
        {isCurrentUser && <OutlinedButton
          icon={<FiEdit3 />}
          iconPosition='leading'
          size='small'
          onClick={
            () => {
              setShowEditSocialLinksLightbox(true);
            }}>
          Edit</OutlinedButton>}
      </div>

      {<ProfileLabelValue label='Linked In' value={freelancer.socialLinks?.linkedin
        ? <a href={freelancer.socialLinks?.linkedin} target="_blank" rel="noopener noreferrer">
          {freelancer.socialLinks?.linkedin}
        </a>
        : 'Not set'} />}

      {(freelancer.socialLinks?.facebook.length || 0) > 0 && <ProfileLabelValue
        label='Facebook'
        value={<a href={freelancer.socialLinks?.facebook} target="_blank" rel="noopener noreferrer">
          {freelancer.socialLinks?.facebook}
        </a>} />}

      {(freelancer.socialLinks?.x.length || 0) > 0 && <ProfileLabelValue
        label='X'
        value={<a href={freelancer.socialLinks?.x} target="_blank" rel="noopener noreferrer">
          {freelancer.socialLinks?.x}
        </a>} />}

      {(freelancer.socialLinks?.instagram.length || 0) > 0 && <ProfileLabelValue
        label='Instagram'
        value={<a href={freelancer.socialLinks?.instagram} target="_blank" rel="noopener noreferrer">
          {freelancer.socialLinks?.instagram}
        </a>} />}

      {(freelancer.socialLinks?.github.length || 0) > 0 && <ProfileLabelValue
        label='Github'
        value={<a href={freelancer.socialLinks?.github} target="_blank" rel="noopener noreferrer">
          {freelancer.socialLinks?.github}
        </a>} />}

      {(freelancer.socialLinks?.youtube.length || 0) > 0 && <ProfileLabelValue
        label='Youtube'
        value={<a href={freelancer.socialLinks?.youtube} target="_blank" rel="noopener noreferrer">
          {freelancer.socialLinks?.youtube}
        </a>} />}

      {(freelancer.socialLinks?.stackoverflow.length || 0) > 0 && <ProfileLabelValue
        label='Stackoverflow'
        value={<a href={freelancer.socialLinks?.stackoverflow} target="_blank" rel="noopener noreferrer">
          {freelancer.socialLinks?.stackoverflow}
        </a>} />}

      {(freelancer.socialLinks?.pinterest.length || 0) > 0 && <ProfileLabelValue
        label='Pinterest'
        value={<a href={freelancer.socialLinks?.pinterest} target="_blank" rel="noopener noreferrer">
          {freelancer.socialLinks?.pinterest}
        </a>} />}

      {(freelancer.socialLinks?.tiktok.length || 0) > 0 && <ProfileLabelValue
        label='Tiktok'
        value={<a href={freelancer.socialLinks?.tiktok} target="_blank" rel="noopener noreferrer">
          {freelancer.socialLinks?.tiktok}
        </a>} />}

      {(freelancer.socialLinks?.reddit.length || 0) > 0 && <ProfileLabelValue
        label='Reddit'
        value={<a href={freelancer.socialLinks?.reddit} target="_blank" rel="noopener noreferrer">
          {freelancer.socialLinks?.reddit}
        </a>} />}

      {(freelancer.socialLinks?.behance.length || 0) > 0 && <ProfileLabelValue
        label='Behance'
        value={<a href={freelancer.socialLinks?.behance} target="_blank" rel="noopener noreferrer">
          {freelancer.socialLinks?.behance}
        </a>} />}

      {(freelancer.socialLinks?.dribbble.length || 0) > 0 && <ProfileLabelValue
        label='Dribbble'
        value={<a href={freelancer.socialLinks?.dribbble} target="_blank" rel="noopener noreferrer">
          {freelancer.socialLinks?.dribbble}
        </a>} />}

      {(freelancer.socialLinks?.websites.length || 0) > 0 && <ProfileLabelValue
        label='Other'
        value={<div className='flex flex-col'>
          {freelancer.socialLinks?.websites.map((url, index) => (
            <a key={index} href={url} target="_blank" rel="noopener noreferrer">
              {url}
            </a>
          ))}
        </div>} />}
    </div>
  );
}


const BusinessInfo: React.FC<{ freelancer: IFreelancer, isCurrentUser: boolean, setShowEditLegalAndTaxLightbox: (show: boolean) => void }> = ({ freelancer, isCurrentUser, setShowEditLegalAndTaxLightbox }) => {
  return (
    <div className='flex flex-col space-y-1'>
      <div className='flex flex-row justify-between items-center'>
        <p className='body2 text-primary-700'>Business Information</p>
        {isCurrentUser && <OutlinedButton
          icon={<FiEdit3 />}
          iconPosition='leading'
          size='small'
          onClick={
            () => {
              setShowEditLegalAndTaxLightbox(true);
            }}>
          Edit</OutlinedButton>}
      </div>

      <ProfileLabelValue label='Full Legal Name' value={freelancer.fullLegalName || 'Not set'} />
      <ProfileLabelValue label='Business Name' value={freelancer.businessName || 'Not set'} />
      <ProfileLabelValue label='Tax Id' value={freelancer.taxId || 'Not set'} />
      <ProfileLabelValue label='Exempt Payee Code' value={freelancer.exemptPayeeCode || 'Not set'} />
      <ProfileLabelValue label='Exemption From FATCA Reporting Code' value={freelancer.exemptionFromFatcaReportingCode || 'Not set'} />
    </div>
  );
}


const WalletInfo: React.FC<{ freelancer: IFreelancer }> = ({ freelancer }) => {

  const { wallet } = useCurrentUser();
  const { config } = useConfig();


  return (
    <div className='flex flex-col space-y-6 py-4'>
      <h4 className='text-primary-700'>Wallet</h4>

      <div className='flex flex-col'>
        <div className='flex flex-row space-x-2 items-center'>
          <h2 className='font-bold'>{(wallet as FreelancerWallet)?.credits !== undefined ? (wallet as FreelancerWallet)?.credits : 0}</h2>
          <p className='body2 text-neutral-900'>Credits</p>
        </div>
        <p className='caption2 font-bold text-neutral-900'>${((wallet as FreelancerWallet)?.credits !== undefined ? (wallet as FreelancerWallet)?.credits : 0) * (config?.rateCreditsToUsd.freelancer || 0)} USD</p>
      </div>

      <OutlinedButton fullWidth onClick={() => { console.log('View Transactions') }}>View Transactions</OutlinedButton>

    </div>
  );
}

const ProfessionalInfo: React.FC<{ freelancer: IFreelancer, isCurrentUser: boolean, setShowEditProfessionalInfoLightbox: (show: boolean) => void }> = ({ freelancer, isCurrentUser, setShowEditProfessionalInfoLightbox }) => {

  const [roles, setRoles] = useState<string[]>([]);

  useEffect(() => {
    const newRoles: string[] = [];
    if (freelancer.restrictedRoles) {
      for (const role of freelancer.restrictedRoles) {
        newRoles.push(UserEnumTextMapper.restrictedFreelancerRole(role));
      }
    }
    if (freelancer.roles) {
      for (const role of freelancer.roles) {
        newRoles.push(UserEnumTextMapper.freelancerRole(role));
      }
    }
    setRoles(newRoles);
  }, [freelancer])


  return (
    <div className='flex flex-col'>
      {/* Bio */}
      <div className='flex flex-row justify-between items-center'>
        <p className='body2 primary-900 font-bold'>Bio</p>
        {isCurrentUser && <OutlinedButton
          icon={<FiEdit3 />}
          iconPosition='leading'
          size='small'
          onClick={
            () => {
              setShowEditProfessionalInfoLightbox(true);
            }}>
          Edit</OutlinedButton>}
      </div>
      <RichText text={freelancer.bio || 'Not set'} />

      {/* Current Occupation */}
      {(freelancer.currentOccupation?.length || 0) > 0 &&
        <div>
          <p className='mt-6 body2 primary-900 font-bold'>Current Occupation</p>
          <p className="">{freelancer.currentOccupation}</p>
        </div>}

      {/* Roles */}
      <div className='mt-6'>
        <p className='body2 primary-900 font-bold'>Roles</p>
        {roles.length > 0
          ? <div className='flex flex-wrap gap-2'>
            {roles.map((role) => (<div key={role} className=''>
              <Chip>
                {role}
              </Chip>
            </div>))}
          </div>
          : <div>No roles set yet</div>}
      </div>

      {/* Industries */}
      {(freelancer.industries?.length || 0) > 0 && <div className='mt-6'>
        <p className='body2 primary-900 font-bold'>Industries</p>
        <div className='flex flex-wrap gap-2'>
          {freelancer.industries!.map((industry) => (<div key={industry} className=''>
            <Chip>
              {industry}
            </Chip>
          </div>))}
        </div>
      </div>}


      {/* Skills */}
      <div className='mt-6'>
        <p className='body2 primary-900 font-bold'>Skills</p>
        <div className='flex flex-col'>
          {(freelancer.skills?.length || 0) > 0
            ? freelancer.skills!.map((skill) => (
              <div key={skill.skill}>
                <SkillExperience
                  skill={skill.skill}
                  experience={skill.experience}
                  experienceLabel={UserEnumTextMapper.freelancerExperienceLevel(skill.experience)}
                />
              </div>
            ))
            : <p className='caption1'>No skills set yet</p>}
        </div>
      </div>

      {/* Tools */}
      <div className='mt-6'>
        <p className='body2 primary-900 font-bold'>Tools</p>
        <div className='flex flex-col'>
          {(freelancer.tools?.length || 0) > 0
            ? freelancer.tools!.map((tool) => (
              <div key={tool.tool}>
                <SkillExperience
                  theme="secondary"
                  skill={tool.tool}
                  experience={tool.experience}
                  experienceLabel={UserEnumTextMapper.freelancerExperienceLevel(tool.experience)}
                />
              </div>))
            : <p className='caption1'>No tools set yet</p>}
        </div>
      </div>
    </div>
  );
}


export default FreelancerUserProfile;